// ============================================================================
// HARDWARE POWER DETECTION
// ============================================================================
// Enable hardware power detection to adjust parameters based on device capabilities
// When enabled, low-power devices will use slower rates to improve performance
const ENABLE_HARDWARE_POWER_DETECTION: boolean = false

function isHardwarePowerDetectionEnabled(): boolean {
  return ENABLE_HARDWARE_POWER_DETECTION
}

/**
 * Detects the device type based on user agent
 */
type DeviceType = 'pc' | 'ios' | 'android'
function detectDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'pc'
  
  const userAgent = window.navigator.userAgent.toLowerCase()
  
  // Check for iOS first (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/i.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  
  // Check for Android
  if (/android/i.test(userAgent)) {
    return 'android'
  }
  
  // Default to PC (desktop/laptop)
  return 'pc'
}

/**
 * Detects browser type
 */
type BrowserType = 'edge' | 'chrome' | 'brave' | 'safari' | 'firefox' | 'other'
function detectBrowserType(): BrowserType {
  if (typeof window === 'undefined') return 'other'
  const userAgent = window.navigator.userAgent.toLowerCase()
  
  // Check for Edge first (Edge also contains 'chrome' in user agent)
  if (userAgent.includes('edge') || userAgent.includes('edg/')) {
    return 'edge'
  }
  
  // Check for Brave (Brave is Chromium-based, identified via navigator.brave)
  if ((navigator as Navigator & { brave?: { isBrave?: () => Promise<boolean> } }).brave) {
    return 'brave'
  }
  
  // Check for Chrome (includes other Chromium browsers that aren't Brave)
  if (userAgent.includes('chrome') && !userAgent.includes('edge')) {
    return 'chrome'
  }
  
  // Check for Safari (Safari doesn't have 'chrome' in user agent but has 'safari')
  if (userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium')) {
    return 'safari'
  }
  
  // Check for Firefox
  if (userAgent.includes('firefox')) {
    return 'firefox'
  }
  
  return 'other'
}

/**
 * Checks if voice speech is supported in the current browser
 * Most modern browsers support Web Speech API
 */
function isVoiceSpeechSupported(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  
  // Check if speech synthesis API exists
  if (!window.speechSynthesis) {
    return false
  }
  
  // Check if we can get voices (some browsers have the API but don't have voices)
  try {
    // The API exists - return true and let the actual speech attempt handle errors
    // This is more permissive and allows browsers like Safari and Brave to work
    return true
  } catch (error) {
    console.warn('Speech synthesis check failed:', error)
    return false
  }
}

/**
 * Detects hardware power level based on available APIs
 */
type HardwarePowerLevel = 'low' | 'medium' | 'high'
function detectHardwarePowerLevel(): HardwarePowerLevel {
  if (!ENABLE_HARDWARE_POWER_DETECTION) {
    return 'medium' // Default to medium if detection is disabled
  }
  
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'medium'
  }
  
  // Check CPU cores (widely supported)
  const cpuCores = navigator.hardwareConcurrency || 2
  
  // Check device memory (limited browser support, mainly Chrome/Edge)
  // deviceMemory is part of the Device Memory API, not in standard Navigator type
  const deviceMemory = ('deviceMemory' in navigator) 
    ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory || null
    : null
  
  // Determine power level
  if (cpuCores < 4 || (deviceMemory && deviceMemory < 4)) {
    return 'low'
  } else if (cpuCores > 6 || (deviceMemory && deviceMemory > 8)) {
    return 'high'
  }
  
  return 'medium'
}

export type { DeviceType, BrowserType, HardwarePowerLevel }
export { detectDeviceType, detectBrowserType, detectHardwarePowerLevel, isHardwarePowerDetectionEnabled, isVoiceSpeechSupported }

// Re-export BrowserType with updated values for type safety